import { Select as BaseSelect } from "@base-ui/react/select";
import checkIcon from "@loot-os/icons/icons/check";
import chevronDownIcon from "@loot-os/icons/icons/chevron-down";

import { cn } from "../foundations/cn.js";
import {
  FIELD_WELL,
  FOCUS_RING,
  POPUP,
  POPUP_ITEM,
  POPUP_MOTION,
} from "../foundations/interaction.js";
import { Icon } from "./Icon.js";

export interface SelectOption<Value extends string = string> {
  value: Value;
  label: string;
}

export interface SelectProps<Value extends string = string> {
  options: readonly SelectOption<Value>[];
  /** The picked value; null while nothing is picked (the placeholder shows), still controlled. */
  value?: Value | null;
  defaultValue?: Value;
  onValueChange?: (value: Value) => void;
  /** The name, e.g. "Sort by". Shown above the trigger unless `hideLabel`. */
  label: string;
  /** Hide the label in a toolbar where the value explains itself; it is still the name. */
  hideLabel?: boolean;
  /** Submits the value with a form under this name. */
  name?: string;
  /** Shown in the trigger until a value is picked. */
  placeholder?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  className?: string;
}

/** Picks one value from a list too long for chips or a segmented control. */
export function Select<Value extends string = string>({
  options,
  value,
  defaultValue,
  onValueChange,
  label,
  hideLabel = false,
  name,
  placeholder,
  disabled,
  defaultOpen,
  className,
}: SelectProps<Value>) {
  return (
    <BaseSelect.Root
      items={options}
      value={value}
      defaultValue={defaultValue}
      defaultOpen={defaultOpen}
      name={name}
      disabled={disabled}
      modal={false}
      onValueChange={(next) => {
        if (next !== null) onValueChange?.(next as Value);
      }}
    >
      <div className={cn("flex flex-col gap-1.5", className)}>
        <BaseSelect.Label
          className={cn(
            "font-body text-body-b3-strong text-text-secondary",
            hideLabel && "sr-only",
          )}
        >
          {label}
        </BaseSelect.Label>
        <BaseSelect.Trigger
          className={cn(
            "group inline-flex h-control-md cursor-pointer items-center justify-between gap-2 pr-2.5 pl-3.5 font-body text-body-b2-strong data-popup-open:bg-bg-hover data-disabled:cursor-default",
            FIELD_WELL,
            FOCUS_RING,
          )}
        >
          <BaseSelect.Value placeholder={placeholder} />
          <BaseSelect.Icon className="shrink-0 text-text-secondary transition-transform duration-fast group-data-popup-open:rotate-180 motion-reduce:transition-none">
            <Icon icon={chevronDownIcon} />
          </BaseSelect.Icon>
        </BaseSelect.Trigger>
      </div>
      <BaseSelect.Portal>
        <BaseSelect.Positioner
          side="bottom"
          align="start"
          sideOffset={4}
          alignItemWithTrigger={false}
          className="z-50 outline-none"
        >
          <BaseSelect.Popup className={cn(POPUP, POPUP_MOTION, "min-w-(--anchor-width)")}>
            {options.map((option) => (
              <BaseSelect.Item
                key={option.value}
                value={option.value}
                className={cn(POPUP_ITEM, "justify-between gap-3 data-selected:text-text-primary")}
              >
                <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
                <BaseSelect.ItemIndicator className="text-accent-text">
                  <Icon icon={checkIcon} />
                </BaseSelect.ItemIndicator>
              </BaseSelect.Item>
            ))}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}
